/**
 * POS Service - Point of Sale Module API Layer
 * 
 * Reference: /docs/SCREEN_API_MAPPING.md > Point Of Sale Module
 * Registry:  /docs/API_REGISTRY.md > Module: Point Of Sale
 */
import apiClient from './apiClient';

// ─── Floor Master (6.1.1) ───────────────────────────────────────────────────
export const floorMaster = {
  getAll: () => apiClient.get('/api/V1/LoadFloorMaster'),
  getById: (floorId) => apiClient.get(`/api/V1/GetFloorDetailsById?floorId=${floorId}`),
  create: (data) => apiClient.post('/api/V1/SaveFloorMaster', data),
  update: (data) => apiClient.put('/api/V1/UpdateFloorMaster', data),
  delete: (floorId) => apiClient.delete(`/api/V1/DeleteFloorMaster?floorId=${floorId}`),
};

// ─── Counter Master (6.1.2) ─────────────────────────────────────────────────
export const counterMaster = {
  getAll: () => apiClient.get('/api/V1/LoadCounterMasters'),
  getById: (counterId) => apiClient.get(`/api/V1/GetCounterDetailsById?counterId=${counterId}`),
  create: (data) => apiClient.post('/api/V1/SaveCounterMaster', data),
  update: (data) => apiClient.put('/api/V1/UpdateCounterMaster', data),
  delete: (counterId) => apiClient.delete(`/api/V1/DeleteCounterMaster?counterId=${counterId}`),
};

// ─── Place Master (6.1.4) ───────────────────────────────────────────────────
export const placeMaster = {
  getAll: () => apiClient.get('/api/PlaceMaster/api/V1/GetAllPlaces'),
  getCountries: () => apiClient.get('/api/PlaceMaster/api/V1/GetAllCountryList'),
  getStates: (countryId) => apiClient.get(`/api/PlaceMaster/api/V1/GetByIdAllStatesList?CountryId=${countryId}`),
  getByCityTable: (cityId) => apiClient.get(`/api/PlaceMaster/api/V1/GetPlaceMasterByIdUsingCityTable?cityId=${cityId}`),
  getById: (cityId) => apiClient.get(`/api/PlaceMaster/api/V1/GetPlaceMasterById?cityId=${cityId}`),
  create: (data) => apiClient.post('/api/PlaceMaster/api/V1/SavePlaceMaster', data),
  update: (data) => apiClient.put('/api/PlaceMaster/api/V1/UpdatePlaceMaster', data),
  delete: (cityId) => apiClient.delete(`/api/PlaceMaster/api/V1/DeletePlaceMaster?cityId=${cityId}`),
};

// ─── Shift Master (6.1.6) ───────────────────────────────────────────────────
export const shiftMaster = {
  getAll: () => apiClient.get('/api/ShiftMaster/api/V1/GetAllShifts'),
  getById: (shiftId) => apiClient.get(`/api/ShiftMaster/api/V1/GetShiftDetailsById?ShiftId=${shiftId}`),
  create: (data) => apiClient.post('/api/ShiftMaster/api/V1/SaveShiftMaster', data),
  update: (data) => apiClient.put('/api/ShiftMaster/api/V1/UpdateShiftMaster', data),
  delete: (shiftId) => apiClient.delete(`/api/ShiftMaster/api/V1/DeleteShiftMaster?shiftId=${shiftId}`),
};

// ─── Salesman Master (6.1.7) ────────────────────────────────────────────────
export const salesmanMaster = {
  getAll: () => apiClient.get('/api/SalesManMaster/api/V1/LoadSalesManMaster'),
  getById: (salesManId) => apiClient.get(`/api/SalesManMaster/api/V1/GetSalesManDetailsById?salesManId=${salesManId}`),
  create: (data) => apiClient.post('/api/SalesManMaster/api/V1/SaveSalesManMaster', data),
  update: (data) => apiClient.put('/api/SalesManMaster/api/V1/UpdateSalesManMaster', data),
  delete: (salesManId) => apiClient.delete(`/api/SalesManMaster/api/V1/DeleteSalesManMaster?salesManId=${salesManId}`),
};

// ─── Customer (6.1.9) ───────────────────────────────────────────────────────
export const customerMaster = {
  getAll: () => apiClient.get('/api/CustomerMaster/api/V1/LoadCustomerMaster'),
  getById: (customerId, flag = 30) => apiClient.get(`/api/CustomerMaster/api/V1/GetCustomerDetailsById?customerId=${customerId}&Flag=${flag}`),
  getCompanyDetails: () => apiClient.get('/api/CustomerMaster/api/V1/LoadCompanyDetails'),
  getAccountDropdown: () => apiClient.get('/api/CustomerMaster/api/V1/GetAccountDropDownDetails'),
  getCurrencyDropdown: () => apiClient.get('/api/CustomerMaster/api/V1/GetCurrencyDropDownDetails'),
  getSalesDivisionDropdown: () => apiClient.get('/api/CustomerMaster/api/V1/GetSalesDivisionDropDownDetails'),
  getSalesRepDropdown: () => apiClient.get('/api/CustomerMaster/api/V1/GetSalesRepDropDownDetails'),
  create: (data) => apiClient.post('/api/CustomerMaster/api/V1/Create', data),
  update: (data) => apiClient.put('/api/CustomerMaster/api/V1/Update', data),
  delete: (customerId) => apiClient.delete(`/api/CustomerMaster/api/V1/Delete?customerId=${customerId}`),
};

// ─── Tender Payment Type (6.1.10) ───────────────────────────────────────────
export const tenderPaymentType = {
  getAll: () => apiClient.get('/api/TenderPaymentType/api/V1/GetAllGridPayments'),
  getGridDetails: (paymentId) => apiClient.get(`/api/TenderPaymentType/api/V1/GetByIdGridPaymentDetails?PaymentId=${paymentId}`),
  getViewDetails: (paymentId) => apiClient.get(`/api/TenderPaymentType/api/V1/GetByIdViewPaymentDetails?PaymentId=${paymentId}`),
  create: (data) => apiClient.post('/api/TenderPaymentType/api/V1/SavePaymentMaster', data),
  update: (data) => apiClient.put('/api/TenderPaymentType/api/V1/UpdatePaymentMaster', data),
  delete: (paymentId) => apiClient.delete(`/api/TenderPaymentType/api/V1/DeletePaymentMaster?paymentId=${paymentId}`),
};

// ─── Sales Order (6.2.2) ────────────────────────────────────────────────────
export const salesOrder = {
  getAll: () => apiClient.get('/api/SalesOrder/api/V1/GetAllDetails'),
  getById: (soNo) => apiClient.get(`/api/SalesOrder/api/V1/GetDetailsById?SONo=${encodeURIComponent(soNo)}`),
  getCustomerList: () => apiClient.get('/api/SalesOrder/api/v1/GetCustomerListDetails'),
  getItemCodeList: () => apiClient.get('/api/SalesOrder/api/v1/GetItemCodeListDetails'),
  create: (data) => apiClient.post('/api/SalesOrder/api/v1/Save', data),
  update: (data) => apiClient.put('/api/SalesOrder/api/v1/Update', data),
};

// ─── Sales Order Cancel (6.2.3) ─────────────────────────────────────────────
export const salesOrderCancel = {
  getList: () => apiClient.get('/api/SalesOrderCancel/api/v1/GetListDetails'),
  getSONoDetails: () => apiClient.get('/api/SalesOrderCancel/api/v1/GetSONoDetails'),
  getByIdDetails: (gpono) => apiClient.get(`/api/SalesOrderCancel/api/v1/GetByIdDetails?GPONO=${encodeURIComponent(gpono)}`),
  getUserBillDetails: (gpono) => apiClient.get(`/api/SalesOrderCancel/api/v1/GetByIdUserBillDetails?GPONO=${encodeURIComponent(gpono)}`),
  getCancelOrders: (soNo) => apiClient.get(`/api/SalesOrderCancel/api/v1/GetByIdCancelOrders?SONo=${encodeURIComponent(soNo)}`),
  loadCancel: (soNo) => apiClient.get(`/api/SalesOrderCancel/api/v1/GetByIdLoadSalesOrderCancel?SONo=${encodeURIComponent(soNo)}`),
  executeCancel: (soNo) => apiClient.delete(`/api/SalesOrderCancel/api/v1/Save?SONo=${encodeURIComponent(soNo)}`),
};

// ─── Delivery Challan (6.4.1) ───────────────────────────────────────────────
export const deliveryChallan = {
  getSalesOrderDetails: () => apiClient.get('/api/DeliveryChallan/api/v1/GetSalesOrderDetails'),
  getGridDetails: () => apiClient.get('/api/DeliveryChallan/api/v1/GetDeliveryChallanGridDetails'),
  getById: (ginvNo) => apiClient.get(`/api/DeliveryChallan/api/v1/GetByIdDeliveryChallanDetails?GINVNO=${encodeURIComponent(ginvNo)}`),
  getEditDetails: (ginvNo) => apiClient.get(`/api/DeliveryChallan/api/v1/EditDeliveryChallanDetails?GINVNO=${encodeURIComponent(ginvNo)}`),
  getCustomerList: () => apiClient.get('/api/DeliveryChallan/api/v1/CustomerListDetails'),
  getItemList: () => apiClient.get('/api/DeliveryChallan/api/v1/ItemListDetails'),
  create: (data) => apiClient.post('/api/DeliveryChallan/api/v1/Save', data),
  update: (data) => apiClient.put('/api/DeliveryChallan/api/v1/Update', data),
};

// ─── Delivery Challan Agst SO (6.4.2) ───────────────────────────────────────
export const deliveryChallanAgstSO = {
  getSalesAccountDetails: () => apiClient.get('/api/DeliveryChallanAgstSO/api/v1/GetSalesAccountDetails'),
  getGridDetails: () => apiClient.get('/api/DeliveryChallanAgstSO/api/v1/DeliveryChallanAgstSOGridDetails'),
  getById: (ginvNo) => apiClient.get(`/api/DeliveryChallanAgstSO/api/v1/GetByIdDeliveryChallanAgstSODetails?GINVNO=${encodeURIComponent(ginvNo)}`),
  getSalesOrderDetails: (sonoId) => apiClient.get(`/api/DeliveryChallanAgstSO/api/v1/GetSalesOrderDetails?SONOId=${encodeURIComponent(sonoId)}`),
  getCustomerList: () => apiClient.get('/api/DeliveryChallanAgstSO/api/v1/CustomerListDetails'),
  getSONOList: (glId) => apiClient.get(`/api/DeliveryChallanAgstSO/api/v1/GetSONOListDetails?GLID=${glId}`),
  getItemList: () => apiClient.get('/api/DeliveryChallanAgstSO/api/v1/ItemListDetails'),
  create: (data) => apiClient.post('/api/DeliveryChallanAgstSO/api/v1/Save', data),
  update: (data) => apiClient.put('/api/DeliveryChallanAgstSO/api/v1/Update', data),
};

// ─── DC Cancellation (6.4.4) ────────────────────────────────────────────────
export const dcCancellation = {
  getDCNoList: (invDate) => apiClient.get(`/api/DeliveryChallanCancellation/api/v1/GetListDCNoDetails?INVDATE=${invDate}`),
  getCancellationDetails: (dcNo) => apiClient.get(`/api/DeliveryChallanCancellation/api/v1/CancellationDCDetails?DCNo=${encodeURIComponent(dcNo)}`),
  getById: (dcNo) => apiClient.get(`/api/DeliveryChallanCancellation/api/v1/GetByIdDCDetails?DCNo=${encodeURIComponent(dcNo)}`),
  managerAuth: (userId, password) => apiClient.get(`/api/DeliveryChallanCancellation/api/v1/ManagerLevelAuthentication?UserId=${userId}&Password=${encodeURIComponent(password)}`),
  save: (data) => apiClient.post('/api/DeliveryChallanCancellation/api/v1/Save', data),
};

// ─── DC Returns (6.4.7) ─────────────────────────────────────────────────────
export const dcReturns = {
  getAll: () => apiClient.get('/api/DCReturns/api/v1/GetDCReturnsListDetails'),
  getById: (dcrNo) => apiClient.get(`/api/DCReturns/api/v1/GetByIdDCReturnsDetails?DCRNo=${encodeURIComponent(dcrNo)}`),
  getItemsByCustomer: (customerId) => apiClient.get(`/api/DCReturns/api/v1/GetByIdItemDetails?CustomerId=${customerId}`),
  getCustomerList: () => apiClient.get('/api/DCReturns/api/v1/CustomerListDetails'),
  getDCNoByCustomer: (customerId) => apiClient.get(`/api/DCReturns/api/v1/GetByIdAgstDCNODetails?CustomerId=${customerId}`),
  getReturnDetails: (dcrNo) => apiClient.get(`/api/DCReturns/api/v1/GetByIdDC_RETURNS?DCRNO=${encodeURIComponent(dcrNo)}`),
  create: (data) => apiClient.post('/api/DCReturns/api/v1/Save', data),
  update: (data) => apiClient.put('/api/DCReturns/api/v1/Update', data),
  delete: (dcrNo) => apiClient.delete(`/api/DCReturns/api/v1/Delete?DCRNo=${encodeURIComponent(dcrNo)}`),
};

// ─── Sales Invoice (6.5.x) ──────────────────────────────────────────────────
export const salesInvoice = {
  getAll: () => apiClient.get('/api/SalesInvoice/api/v1/GetAll'),
  getById: (invNo) => apiClient.get(`/api/SalesInvoice/api/v1/GetById?invNo=${encodeURIComponent(invNo)}`),
  getCustomerList: () => apiClient.get('/api/SalesInvoice/api/v1/GetCustomerList'),
  getItemList: () => apiClient.get('/api/SalesInvoice/api/v1/GetItemList'),
  getDCList: (customerId) => apiClient.get(`/api/SalesInvoice/api/v1/GetDCList?customerId=${customerId}`),
  create: (data) => apiClient.post('/api/SalesInvoice/api/v1/Save', data),
  update: (data) => apiClient.put('/api/SalesInvoice/api/v1/Update', data),
  print: (invNo) => apiClient.get(`/api/SalesInvoice/api/v1/Print?invNo=${encodeURIComponent(invNo)}`),
};

// ─── POS Billing (6.6.x) ────────────────────────────────────────────────────
export const posBilling = {
  createBill: (data) => apiClient.post('/api/POSBilling/api/V1/SaveBill', data),
  updateBill: (data) => apiClient.put('/api/POSBilling/api/V1/UpdateBill', data),
  getBillById: (billNo) => apiClient.get(`/api/POSBilling/api/V1/GetBillById?billNo=${encodeURIComponent(billNo)}`),
  getBillHistory: (params) => apiClient.get('/api/POSBilling/api/V1/GetBillHistory', { params }),
  getItemByBarcode: (barcode) => apiClient.get(`/api/POSBilling/api/V1/GetItemByBarcode?barcode=${encodeURIComponent(barcode)}`),
  getItemBySKU: (sku) => apiClient.get(`/api/POSBilling/api/V1/GetItemBySKU?sku=${encodeURIComponent(sku)}`),
  searchItems: (query) => apiClient.get(`/api/POSBilling/api/V1/SearchItems?query=${encodeURIComponent(query)}`),
  getCounterInfo: () => apiClient.get('/api/POSBilling/api/V1/GetCounterInfo'),
  getShiftInfo: () => apiClient.get('/api/POSBilling/api/V1/GetShiftInfo'),
  getTellerList: () => apiClient.get('/api/POSBilling/api/V1/GetTellerList'),
  getSalesmanList: () => apiClient.get('/api/POSBilling/api/V1/GetSalesmanList'),
  holdBill: (data) => apiClient.post('/api/POSBilling/api/V1/HoldBill', data),
  getHeldBills: () => apiClient.get('/api/POSBilling/api/V1/GetHeldBills'),
  resumeHeldBill: (holdId) => apiClient.get(`/api/POSBilling/api/V1/ResumeHeldBill?holdId=${holdId}`),
  applyItemDiscount: (data) => apiClient.post('/api/POSBilling/api/V1/ApplyItemDiscount', data),
  applyBillDiscount: (data) => apiClient.post('/api/POSBilling/api/V1/ApplyBillDiscount', data),
  getPaymentModes: () => apiClient.get('/api/POSBilling/api/V1/GetPaymentModes'),
  processPayment: (data) => apiClient.post('/api/POSBilling/api/V1/ProcessPayment', data),
  printBill: (billNo) => apiClient.get(`/api/POSBilling/api/V1/PrintBill?billNo=${encodeURIComponent(billNo)}`),
};
